import React, { useState, useMemo, useCallback, useEffect } from 'react';
import DraggableWindow from './DraggableWindow';
import { queryService, lojasService } from '../../services/api';
import {
  gerarImagemProtocoloDataUrl,
  imprimirProtocoloDataUrl,
  baixarProtocoloDataUrl,
} from '../../utils/protocoloImage';

const TIPOS = [
  { id: 'protocolo', label: 'Protocolo', icon: 'fa-receipt', placeholder: 'Ex: 452837' },
  { id: 'chave',     label: 'Chave NFe', icon: 'fa-key',     placeholder: '44 dígitos da chave NFe' },
  { id: 'pedido',    label: 'Pedido',    icon: 'fa-file-invoice', placeholder: 'Ex: 3304139' },
  { id: 'produto',   label: 'Produto',   icon: 'fa-box',     placeholder: 'Código do produto' },
];

const formatDateBr = (raw) => {
  if (!raw) return '-';
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleDateString('pt-BR');
  } catch {
    return String(raw);
  }
};

const formatDateTimeBr = (raw) => {
  if (!raw) return '-';
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleString('pt-BR');
  } catch {
    return String(raw);
  }
};

const STATUS_LABEL = {
  1: 'Aberto',
  2: 'Em Trânsito',
  3: 'Recebido',
};

const WindowBuscaProtocolo = ({ onClose, onMinimize, isMinimized, zIndex, onFocus }) => {
  const [tipo, setTipo] = useState('protocolo');
  const [termo, setTermo] = useState('');
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [lojas, setLojas] = useState([]);

  const [detalheAberto, setDetalheAberto] = useState(false);
  const [detalheDados, setDetalheDados] = useState(null);
  const [gerandoImagem, setGerandoImagem] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await lojasService.getLojas();
        setLojas(Array.isArray(data) ? data : []);
      } catch (e) { /* silencioso */ }
    })();
  }, []);

  const lojaNome = useCallback((cdfil) => {
    const loja = lojas.find((l) => String(l.numero) === String(cdfil) || String(l.id) === String(cdfil));
    return loja?.nome || '';
  }, [lojas]);

  const tipoAtual = useMemo(() => TIPOS.find((t) => t.id === tipo) || TIPOS[0], [tipo]);

  const buscar = async (e) => {
    if (e) e.preventDefault();
    setErro('');
    setResultados([]);

    const termoLimpo = termo.trim();
    if (!termoLimpo) {
      setErro('Informe um valor para buscar.');
      return;
    }

    setLoading(true);
    try {
      let query = '';
      const escTermo = termoLimpo.replace(/'/g, "''");

      if (tipo === 'protocolo') {
        query = `
          SELECT
            po.id AS protocolo,
            pod.documento,
            po.remetente_cdfun,
            po.remetente_cdfil,
            po.destinatario_cdfun,
            po.destinatario_cdfil,
            po.status,
            po.data_cadastro,
            (SELECT COUNT(*) FROM protocolo_online_documento pod_count
              WHERE pod_count.protocolo = po.id AND pod_count.documento_tipo = 1) AS quantidade_chaves,
            scefun.NOME AS nome_funcionario,
            scefun_dest.NOME AS nome_destinatario
          FROM protocolo_online po
          LEFT JOIN protocolo_online_documento pod ON pod.protocolo = po.id
          LEFT JOIN scefun ON scefun.CDFUN = po.remetente_cdfun
          LEFT JOIN scefun scefun_dest ON scefun_dest.CDFUN = po.destinatario_cdfun
          WHERE po.id LIKE '%${escTermo}%'
          ORDER BY po.data_cadastro DESC
          LIMIT 200
        `;
      } else if (tipo === 'chave') {
        query = `
          SELECT
            po.id AS protocolo,
            pod.documento,
            po.remetente_cdfun,
            po.remetente_cdfil,
            po.destinatario_cdfun,
            po.destinatario_cdfil,
            po.status,
            po.data_cadastro,
            (SELECT COUNT(*) FROM protocolo_online_documento pod_count
              WHERE pod_count.protocolo = po.id AND pod_count.documento_tipo = 1) AS quantidade_chaves,
            scefun.NOME AS nome_funcionario,
            scefun_dest.NOME AS nome_destinatario
          FROM protocolo_online_documento pod
          JOIN protocolo_online po ON po.id = pod.protocolo
          LEFT JOIN scefun ON scefun.CDFUN = po.remetente_cdfun
          LEFT JOIN scefun scefun_dest ON scefun_dest.CDFUN = po.destinatario_cdfun
          WHERE pod.documento LIKE '%${escTermo}%'
          ORDER BY po.data_cadastro DESC
          LIMIT 200
        `;
      } else if (tipo === 'pedido') {
        // Pedido → busca a chave NFE em admnf, depois protocolo
        const queryChave = `
          SELECT NRNFE
          FROM admnf
          WHERE NRPEDIDO = ${Number(escTermo) || 0}
          ORDER BY DTEMIS DESC
          LIMIT 1
        `;
        const resChave = await queryService.execute(queryChave);
        if (!resChave || resChave.length === 0 || !resChave[0]?.NRNFE) {
          setErro('Nenhuma NFe encontrada para esse pedido.');
          setLoading(false);
          return;
        }
        const chave = resChave[0].NRNFE;
        query = `
          SELECT
            po.id AS protocolo,
            pod.documento,
            po.remetente_cdfun,
            po.remetente_cdfil,
            po.destinatario_cdfun,
            po.destinatario_cdfil,
            po.status,
            po.data_cadastro,
            (SELECT COUNT(*) FROM protocolo_online_documento pod_count
              WHERE pod_count.protocolo = po.id AND pod_count.documento_tipo = 1) AS quantidade_chaves,
            scefun.NOME AS nome_funcionario,
            scefun_dest.NOME AS nome_destinatario
          FROM protocolo_online_documento pod
          JOIN protocolo_online po ON po.id = pod.protocolo
          LEFT JOIN scefun ON scefun.CDFUN = po.remetente_cdfun
          LEFT JOIN scefun scefun_dest ON scefun_dest.CDFUN = po.destinatario_cdfun
          WHERE pod.documento = '${chave}'
          ORDER BY po.data_cadastro DESC
          LIMIT 200
        `;
      } else if (tipo === 'produto') {
        // Produto → busca itens em admnfi pelo CDPRODU, agrupa por NRNFE,
        // depois busca protocolos com essa chave
        query = `
          SELECT
            po.id AS protocolo,
            pod.documento,
            po.remetente_cdfun,
            po.remetente_cdfil,
            po.destinatario_cdfun,
            po.destinatario_cdfil,
            po.status,
            po.data_cadastro,
            (SELECT COUNT(*) FROM protocolo_online_documento pod_count
              WHERE pod_count.protocolo = po.id AND pod_count.documento_tipo = 1) AS quantidade_chaves,
            scefun.NOME AS nome_funcionario,
            scefun_dest.NOME AS nome_destinatario
          FROM protocolo_online_documento pod
          JOIN protocolo_online po ON po.id = pod.protocolo
          LEFT JOIN scefun ON scefun.CDFUN = po.remetente_cdfun
          LEFT JOIN scefun scefun_dest ON scefun_dest.CDFUN = po.destinatario_cdfun
          WHERE pod.documento IN (
            SELECT DISTINCT admnf.NRNFE
            FROM admnfi
            JOIN admnf ON admnf.NRPEDIDO = admnfi.NRPEDIDO
            WHERE admnfi.CDPRODU = '${escTermo}'
            AND admnf.NRNFE IS NOT NULL
            AND admnf.NRNFE <> ''
          )
          ORDER BY po.data_cadastro DESC
          LIMIT 200
        `;
      }

      const data = await queryService.execute(query);
      const arr = Array.isArray(data) ? data : [];
      // dedupe por protocolo+documento
      const seen = new Set();
      const dedup = [];
      for (const r of arr) {
        const k = `${r.protocolo}|${r.documento || ''}`;
        if (!seen.has(k)) {
          seen.add(k);
          dedup.push(r);
        }
      }
      setResultados(dedup);
      if (dedup.length === 0) {
        setErro('Nenhum protocolo encontrado.');
      }
    } catch (err) {
      console.error('Erro busca protocolo:', err);
      setErro('Erro ao buscar: ' + (err?.message || ''));
    } finally {
      setLoading(false);
    }
  };

  const abrirDetalhe = (item) => {
    setDetalheDados(item);
    setDetalheAberto(true);
    setPreviewUrl(null);
  };

  const fecharDetalhe = () => {
    setDetalheAberto(false);
    setDetalheDados(null);
    setPreviewUrl(null);
    setGerandoImagem(false);
  };

  const gerarImagem = async () => {
    if (!detalheDados?.protocolo) return;
    setGerandoImagem(true);
    try {
      const url = await gerarImagemProtocoloDataUrl({
        ...detalheDados,
        lojaNomeResolver: lojaNome,
      });
      setPreviewUrl(url);
    } catch (e) {
      console.error('Erro gerar protocolo:', e);
      alert('Erro ao gerar imagem do protocolo.');
    } finally {
      setGerandoImagem(false);
    }
  };

  const imprimir = () => imprimirProtocoloDataUrl(previewUrl);
  const baixar = () => baixarProtocoloDataUrl(previewUrl, `protocolo-${detalheDados?.protocolo}.png`);

  return (
    <DraggableWindow
      title="Buscar Protocolo"
      icon="fa-magnifying-glass"
      onClose={onClose}
      onMinimize={onMinimize}
      isMinimized={isMinimized}
      zIndex={zIndex}
      onFocus={onFocus}
      initialWidth={920}
      initialHeight={620}
      initialTop={60}
      initialLeft={140}
    >
      <div className="busca-protocolo-wrapper">
        <form onSubmit={buscar} className="busca-protocolo-form">
          <div className="busca-protocolo-tipos">
            {TIPOS.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`busca-protocolo-tipo ${tipo === t.id ? 'active' : ''}`}
                onClick={() => { setTipo(t.id); setResultados([]); setErro(''); }}
              >
                <i className={`fas ${t.icon}`}></i>
                {t.label}
              </button>
            ))}
          </div>

          <div className="busca-protocolo-input-row">
            <input
              type="text"
              value={termo}
              onChange={(e) => setTermo(e.target.value)}
              placeholder={tipoAtual.placeholder}
              autoFocus
              className="busca-protocolo-input"
            />
            <button type="submit" className="consulta-btn-submit" disabled={loading || !termo.trim()}>
              <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-magnifying-glass'}`}></i>
              {loading ? ' Buscando' : ' Buscar'}
            </button>
          </div>
        </form>

        {erro && !loading && (
          <div className="consulta-error" style={{ marginTop: 10 }}>{erro}</div>
        )}

        {resultados.length > 0 && (
          <div className="busca-protocolo-results">
            <div className="busca-protocolo-results-header">
              <strong>{resultados.length}</strong> resultado{resultados.length > 1 ? 's' : ''}
            </div>
            <div className="busca-protocolo-table-wrap">
              <table className="busca-protocolo-table">
                <thead>
                  <tr>
                    <th>Protocolo</th>
                    <th>Documento</th>
                    <th>Remetente</th>
                    <th>Data</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {resultados.map((r, idx) => (
                    <tr
                      key={`${r.protocolo}-${r.documento || ''}-${idx}`}
                      onClick={() => abrirDetalhe(r)}
                      className="busca-protocolo-row"
                    >
                      <td className="mono">{r.protocolo}</td>
                      <td className="mono ellipsis">{r.documento || '-'}</td>
                      <td>{r.nome_funcionario || r.remetente_cdfun || '-'}</td>
                      <td>{formatDateBr(r.data_cadastro)}</td>
                      <td>
                        <span className={`busca-protocolo-status status-${r.status}`}>
                          {STATUS_LABEL[r.status] || r.status || '-'}
                        </span>
                      </td>
                      <td className="text-right">
                        <button
                          type="button"
                          className="ios-btn-sm tint-blue"
                          onClick={(e) => { e.stopPropagation(); abrirDetalhe(r); }}
                        >
                          <i className="fas fa-arrow-right"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {detalheAberto && detalheDados && (
        <div className="caixas-sheet-backdrop" onClick={fecharDetalhe}>
          <div className="caixas-sheet busca-protocolo-detail" onClick={(e) => e.stopPropagation()}>
            <div className="caixas-sheet-header">
              <div className="caixas-sheet-title">
                <i className="fas fa-receipt"></i>
                <div>
                  <div className="caixas-sheet-title-main">Protocolo {detalheDados.protocolo}</div>
                  <div className="caixas-sheet-title-sub">
                    {STATUS_LABEL[detalheDados.status] || detalheDados.status || '-'} ·{' '}
                    {formatDateTimeBr(detalheDados.data_cadastro)}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="caixas-sheet-close"
                onClick={fecharDetalhe}
                aria-label="Fechar"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="caixas-sheet-body">
              <div className="busca-protocolo-detail-grid">
                <div className="busca-protocolo-detail-item">
                  <span>Chave NFe</span>
                  <strong className="mono ellipsis">{detalheDados.documento || '-'}</strong>
                </div>
                <div className="busca-protocolo-detail-item">
                  <span>Remetente</span>
                  <strong>
                    {detalheDados.remetente_cdfun}
                    {detalheDados.nome_funcionario ? ` — ${detalheDados.nome_funcionario}` : ''}
                  </strong>
                </div>
                <div className="busca-protocolo-detail-item">
                  <span>Loja Remetente</span>
                  <strong>
                    {detalheDados.remetente_cdfil}
                    {lojaNome(detalheDados.remetente_cdfil) ? ` - ${lojaNome(detalheDados.remetente_cdfil)}` : ''}
                  </strong>
                </div>
                <div className="busca-protocolo-detail-item">
                  <span>Loja Destino</span>
                  <strong>
                    {detalheDados.destinatario_cdfil || '-'}
                    {lojaNome(detalheDados.destinatario_cdfil) ? ` - ${lojaNome(detalheDados.destinatario_cdfil)}` : ''}
                  </strong>
                </div>
                <div className="busca-protocolo-detail-item">
                  <span>Destinatário</span>
                  <strong>
                    {detalheDados.destinatario_cdfun || '-'}
                    {detalheDados.nome_destinatario ? ` — ${detalheDados.nome_destinatario}` : ''}
                  </strong>
                </div>
                <div className="busca-protocolo-detail-item">
                  <span>Data</span>
                  <strong>{formatDateTimeBr(detalheDados.data_cadastro)}</strong>
                </div>
              </div>

              <div className="busca-protocolo-detail-actions">
                {!previewUrl && (
                  <button
                    type="button"
                    className="ios-btn-sm tint-blue"
                    onClick={gerarImagem}
                    disabled={gerandoImagem}
                  >
                    <i className={`fas ${gerandoImagem ? 'fa-spinner fa-spin' : 'fa-image'}`}></i>
                    {gerandoImagem ? ' Gerando...' : ' Gerar Imagem'}
                  </button>
                )}
                {previewUrl && (
                  <>
                    <button type="button" className="ios-btn-sm" onClick={() => setPreviewUrl(null)}>
                      <i className="fas fa-rotate-left"></i> Limpar
                    </button>
                    <button type="button" className="ios-btn-sm tint-blue" onClick={imprimir}>
                      <i className="fas fa-print"></i> Imprimir (80mm)
                    </button>
                    <button type="button" className="ios-btn-sm solid-blue" onClick={baixar}>
                      <i className="fas fa-download"></i> Baixar PNG
                    </button>
                  </>
                )}
              </div>

              {previewUrl && (
                <div className="busca-protocolo-preview">
                  <img src={previewUrl} alt="Protocolo" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </DraggableWindow>
  );
};

export default WindowBuscaProtocolo;
